/**
 * Logic for validating connections between nodes in the workflow builder.
 * 
 * Rules:
 * - Trigger: Cannot have incoming edges.
 * - Time Delay: Must follow Trigger, Email, SMS, or Condition nodes.
 * - Email/SMS: Can follow Trigger, Delay, Condition, or other Email/SMS nodes.
 * - Condition: Can follow Trigger, Delay, Email, SMS, or other Condition nodes.
 * - General: Prevent self-loops.
 */

export const validateConnection = (params, nodes, edges, workflowName = '') => {
  const { source, target } = params;
  
  if (source === target) {
    return { isValid: false, message: 'Cannot connect a node to itself.' };
  }

  const sourceNode = nodes.find(n => n.id === source);
  const targetNode = nodes.find(n => n.id === target);

  if (!sourceNode || !targetNode) {
    return { isValid: false, message: 'Invalid nodes.' };
  }

  // Rule: Trigger cannot be a target
  if (targetNode.type === 'triggerNode') {
    return { isValid: false, message: 'Trigger nodes cannot have incoming connections.' };
  }

  // Check if this is the Demo Fallback Workflow for special rules
  const isDemoFallback = workflowName === 'Demo Fallback Workflow';

  // Rule: Condition
  if (targetNode.type === 'conditionNode') {
    const validSources = ['triggerNode', 'waitNode', 'emailNode', 'smsNode', 'conditionNode', 'demoFallbackActionNode'];
    if (!validSources.includes(sourceNode.type)) {
      return {
        isValid: false,
        message: 'Condition nodes can only be connected after Trigger, Time delay, Email, SMS, or Condition nodes.',
      };
    }
  }

  // Rule: Time Delay
  if (targetNode.type === 'waitNode') {
    // Must come after Trigger, Email, SMS, Condition, or Label (if Demo Fallback)
    const validSources = ['triggerNode', 'emailNode', 'smsNode', 'conditionNode'];
    if (isDemoFallback) {
      validSources.push('labelNode');
    }

    if (!validSources.includes(sourceNode.type)) {
      return { 
        isValid: false, 
        message: `Time delay nodes can only be connected after Trigger, Email, or SMS nodes${isDemoFallback ? ' or Description nodes' : ''}.` 
      };
    }
  }

  // Rule: Email/SMS/Label
  const interactionNodes = ['emailNode', 'smsNode', 'labelNode'];
  if (interactionNodes.includes(targetNode.type)) {
    // Can come after Trigger, Wait, Email, SMS, Condition, or Label (if Demo Fallback)
    const validSources = ['triggerNode', 'waitNode', 'emailNode', 'smsNode', 'conditionNode', 'demoFallbackActionNode'];
    if (isDemoFallback) {
      validSources.push('labelNode');
    }

    if (!validSources.includes(sourceNode.type)) {
       return { 
        isValid: false, 
        message: 'This node cannot be connected here.' 
      };
    }
  }

  return { isValid: true };
};
